import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import _ from 'lodash';
import { useSportsRealtimeResults } from 'projectSpecific/sports/useHelpers/useSportRealtimeResults';
import { useSportResults } from 'projectSpecific/sports/useHelpers/useSportResults';
import { _sportsInfoTypes, useSportsInfo } from 'projectSpecific/sports/useHelpers/useSportsInfo';
import React, { useContext, useEffect, useMemo, useReducer } from "react";
import { FrameworkContext } from '../../../../global/cnr/contexts/FrameworkContent';
import { HeaderContext } from '../../../../global/cnr/contexts/HeaderContext';
import { ParentContext } from '../../../../global/cnr/contexts/ParentContext';
import { TrackerContext } from '../../../../global/cnr/contexts/TrackerContext';
import { _currentUserIdProp } from '../../../../global/cnr/reducers/AppUserReducer';
import { frameworkRightTypes } from '../../../../global/cnr/reducers/FrameworkReducer';
import { gEnums } from '../../../../global/enums/globalEnums';
import { currentHelpers } from '../../../../global/redirection/current';
import { realtimeDocumentTypes } from '../reducers/SportsEditMatchReducer';
import { sportsSeasonHandlers, sportsSeasonInitialState, sportsSeasonReducer } from '../reducers/SportsSeasonReducer';
import { SportsDistrictContext } from './SportsDistrictContext';
import { SportsOrganizationContext } from './SportsOrganizationContext';

const _sportSeasonSettings = {
  useScoreBlock: false,
  useScoreTeamBlock: false,
  startHour: 19,
  usePlayoffBrackets: true,
}

const _sportsButtonProps = {
  color: 'teal',
  icon: 'snowflake outline',
}

const _sportFormProps = {
  access: {
    accessLevel: 'accessLevel',
  },
  glsAccess: {
    googleSheetsId: 'googleSheetsId',
    schoolsAccessSheet: 'schoolsAccessSheet',
    sportsAccessSheet: 'sportsAccessSheet',
    teamsAccessSheet: 'teamsAccessSheet',
    schoolsContactSheet: 'schoolsContactSheet',
    districtContactSheet: 'districtContactSheet',
  },
  glsDistrict: {
    googleSheetsId: 'googleSheetsId',
  },
  glsScores: {
    districtLink: 'districtLink',
    googleSheetsId: 'googleSheetsId',
    rankingsGoogleSheetsId: 'rankingsGoogleSheetsId',
    rosterGoogleSheetsId: 'rosterGoogleSheetsId',
    mediaUrl: 'mediaUrl',
    mediaUrlYearReplace: 'mediaUrlYearReplace',
    mediaUrlDateReplace: 'mediaUrlDateReplace',
    playoffScoresSheet: 'playoffScoresSheet',
    rankingsSheet: 'rankingsSheet',
    scoresSheet: 'scoresSheet',
    validAdmins: 'validAdmins',
    scoreEmails: 'scoreEmails',
    allowScoreUpdateFromGs: 'allowScoreUpdateFromGs',
    allowScheduledScoreUpdateFromGs: 'allowScheduledScoreUpdateFromGs',
    sortStandingsByPoints: 'sortStandingsByPoints',
  },
  rankingLinks: {
    googleSheetsId: 'googleSheetsId',
    rankingsSheet: 'rankingsSheet',
  }
}

export const _sportsFormProps = {
  access: {
    accessLevel: 'accessLevel',
  },
  accessTeam: {
    accessTeam: 'accessTeam',
  },
  accessSports: {
    accessSports: 'accessSports',
  },
  scoreAccess: {
    scoreAccess: 'scoreAccess',
  },
  accessSchools: {
    accessSchools: 'accessSchools',
  },
  contactsSchools: {
    email: 'email',
    pushApi: 'pushApi',
    pullUrl: 'pullUrl',
  },
  contactsDistrict: {
    email: 'email',
    pushApi: 'pushApi',
    pullUrl: 'pullUrl',
  },
  emailSchools: {
    pushApi: 'email',
  },
  glsTeam: {
    googleSheetsId: 'googleSheetsId',
    rosterSheet: 'rosterSheet',
    staffSheet: 'staffSheet',
    newsSheet: 'newsSheet',
    youTubeLink: 'youTubeLink',
    twitterLink: 'twitterLink',
  },
  sportsSettings: {
    allowScoreUpdateFromGs: 'allowScoreUpdateFromGs',
  },
  tournament: {
    name: 'name',
    startDate: 'startDate',
    startTime: 'startTime',
    results: 'results',
    url: 'url',
    link: 'link',
  },
  news: {
    title: 'title',
    body: 'body',
  },
  updateDistrictSeasonScores: { ..._sportFormProps.glsScores },
  glsAccess: { ..._sportFormProps.glsAccess },
  glsDistrict: { ..._sportFormProps.glsDistrict },
  glsScores: { ..._sportFormProps.glsScores },
  scoreLink: { ..._sportFormProps.glsScores },
  seasonResults: { ..._sportFormProps.glsScores },
  seasonRankings: { ..._sportFormProps.glsScores },
  useasonRankings: { ..._sportFormProps.rankingLinks },
  createSeasonFromGs: {
    googleSheetsId: 'googleSheetsId',
    sheetName: 'sheetName',
  }
}

export const matchScheduleTypes = {
  date: 'date',
  latest: 'latest',
  next: 'next',
  overview: 'overview',
  levelMatches: 'levelMatches'
}

const _applyUseSportsInfo = false

export const SportsSeasonContext = React.createContext()

export default function SportsSeasonProvider(props) {

  // parentContext
  const parentContext = useContext(ParentContext)
  const { states, handlers, fns, parent_fns, fn_cls } = parentContext ?? {}
  const { app_state, appUser_state, paps_state, currentSettings_state, page_state, serviceWorker_state } = states ?? {}
  const { appUser_handlers, serviceWorker_handlers } = handlers ?? {}
  const { mainRt } = app_state
  const { view, pathViews, viewKey } = paps_state ?? {}
  const { database_fns } = fns ?? {}

  // appUserContext
  const { appUser, accessClone } = appUser_state ?? {}
  const { uid, email: email_appUser } = appUser ?? {}
  const appUserAccess = useAppUserAccess()
  const { isAdminOrSuper, isAppUser, accessLevel } = appUserAccess ?? {}
  const _appUserCanModify = accessLevel >= gEnums.accessLevels.appProfile.value

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state, framework_handlers } = frameworkContext ?? {}
  const { frameworkRightType } = framework_state ?? {}

  const trackerContext = useContext(TrackerContext)
  const { tracker_state, tracker_handlers } = trackerContext ?? {}
  const { handle_track } = tracker_handlers ?? {}

  // sportsOrganizationContext
  const sportsOrganizationContext = useContext(SportsOrganizationContext)
  const { sportsOrganization_state } = sportsOrganizationContext ?? {}
  const { districts: districts_org, schools: schools_org, useAppDarkMode } = sportsOrganization_state ?? {}

  // sportsDistrictContext
  const sportsDistrictContext = useContext(SportsDistrictContext)
  const { sportsDistrict_state } = sportsDistrictContext ?? {}
  const { district_data, sportsAccess_district } = sportsDistrict_state ?? {}
  const { sports, schools: schools_district, gls_scores, sportsTeams, sportsAccess_sports: sportsAccess_sports_d } = district_data ?? {}
  const { sports: access_sports, schools: access_schools } = sportsAccess_district ?? {}
  const sportTeams = sportsTeams && sportsTeams[pathViews.sports] ? sportsTeams[pathViews.sports].teams : {}

  // sportsSeasonDataContext
  // const sportsSeasonDataContext = useContext(SportsSeasonDataContext)
  // const { sportsSeasonData_state } = sportsSeasonDataContext ?? {}
  // const { sportsInfo, sportsResultsInfo, sportsRealtimeResultsInfo } = sportsSeasonData_state ?? {}

  // pageContext 
  const { aps_global, aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});
  const { sportData: sportDataSettings, sportSettings, sportPermissions } = aps_global ?? {}

  const _currentUserId = currentHelpers.storageItem_get(_currentUserIdProp)

  const { cache_info } = serviceWorker_state ?? {}
  const { cache_groups } = cache_info ?? {}
  const { cache_sport } = cache_groups ?? {}

  const {
    rtAdminOnly,
    rtAppUserOnly,
    useRtMatchData,
    useRtMatchDataView,
    rtMatchUpdates,
  } = sportDataSettings ?? {}

  const getSportData = () => {

    let _rtInfo = {
      useRtMatchData: useRtMatchData,
      useRtMatchDataView: useRtMatchDataView,
      rtMatchUpdates: rtMatchUpdates,
    }

    if (rtAdminOnly) {
      _rtInfo = {
        useRtMatchData: isAdminOrSuper && useRtMatchData,
        useRtMatchDataView: isAdminOrSuper && useRtMatchDataView,
        rtMatchUpdates: isAdminOrSuper && rtMatchUpdates,
      }
    }

    if (rtAppUserOnly) {
      _rtInfo = {
        useRtMatchData: isAppUser && useRtMatchData,
        useRtMatchDataView: isAppUser && useRtMatchDataView,
        rtMatchUpdates: isAppUser && rtMatchUpdates,
      }
    }

    return _rtInfo

  }

  // const _matchScheduleDisplayType = matchScheduleDisplayType ? matchScheduleDisplayType : gEnums.matchScheduleDisplayTypes.selectedDate

  const headerContext = useContext(HeaderContext)
  const { header_handlers } = headerContext ?? {}

  const { viewItems: viewItems_page } = aps_page ?? {}

  let viewItem = viewItems_page && viewItems_page[view] ? viewItems_page[view] : {}
  const { dataSource } = viewItem ?? {}
  const { dataParents, useSeasonals } = dataSource ?? {}

  let ownerPath;

  if (pathViews) {
    if (pathViews.schools) { ownerPath = 'schools' }
    if (pathViews.districts) { ownerPath = 'districts' }
    if (pathViews.organizations) { ownerPath = 'organizations' }
  }

  const sportsMode = {
    organizations: pathViews && pathViews.organizations && !pathViews.districts && !pathViews.schools && !pathViews.matches && !pathViews.sports && !pathViews.teams ? true : false,
    districts: pathViews && pathViews.districts && !pathViews.schools && !pathViews.matches && !pathViews.sports && !pathViews.teams ? true : false,
    matches: pathViews && pathViews.matches ? true : false,
    schools: pathViews && pathViews.schools ? true : false,
    sports: pathViews && pathViews.sports && !pathViews.teams ? true : false,
    teams: pathViews && pathViews.teams ? true : false,
    sportLanding: pathViews && pathViews.sports && !pathViews.teams && !pathViews.matches
  }

  const _cacheData = cache_sport && cache_sport[viewKey]

  const _sportData = getSportData()

  const init_state = {
    _cacheData, // ✅
    _sportData,
    _sportSeasonSettings,
    appUserCanModify: _appUserCanModify,
    appUserAccess,
    cssOpts: { name: 'ssc-normal' },
    currentUserId: _currentUserId,
    handle_track,
    inverted: false,
    sportSettings_info: sportSettings,
    pathViews,
    sportDataSettings,
    sportPermissions,
    sportsButtonProps: _sportsButtonProps,
    sportsMode,
    uid, ownerPath,
    useAppDarkMode,
    useSeasonals,
    viewItem,
  }

  const [sportsSeason_state, sportsSeason_dispatch] = useReducer(sportsSeasonReducer, init_state, sportsSeasonInitialState)
  const sportsSeason_handlers = useMemo(
    () => sportsSeasonHandlers(sportsSeason_dispatch),
    [sportsSeason_dispatch]
  );

  const {
    _sportsModeKey,
    matches_info,
    sportsAccess_sports,
    sportAccess,
    results_matchDay,
    results_realtime,
    sportsAccess_teams,
    currentSeason_info
  } = sportsSeason_state ?? {}

  const { latestSeason, sportsKey } = currentSeason_info ?? {}

  const sportsInfo = useSportsInfo({ sportsInfoType: _sportsInfoTypes.sportsSeason, sports, sportsMode })
  const sportsResultsInfo = useSportResults({ sportsInfoType: _sportsInfoTypes.sportsSeason })
  const sportsRealtimeResultsInfo = useSportsRealtimeResults({})

  useEffect(() => {
    if (sportsInfo) {
      sportsSeason_handlers.handleSet_sportsInfo(sportsInfo)
    }
  }, [sportsInfo])

  useEffect(() => {
    if (sportsResultsInfo) {
      sportsSeason_handlers.handleSet_sportsResultsInfo(sportsResultsInfo)
    }
  }, [sportsResultsInfo])

  useEffect(() => {
    if (sportsInfo) {
      sportsSeason_handlers.handleSet_sportsRealtimeResultsInfo(sportsRealtimeResultsInfo)
    }
  }, [sportsRealtimeResultsInfo])

  const findItemsByName = (obj, name) => {
    let allowedItems = {};

    function search(obj, parentKey) {
      _.forEach(obj, (value, key) => {
        if (_.isObject(value)) {
          if (value.name === name) {
            allowedItems[parentKey] = value;
          }
          search(value, key);
        }
      });
    }

    search(obj);

    return allowedItems;
  }

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     sportsSeason_handlers.handle_testSeason()
  //   }, 30000); // 10 seconds

  //   return () => clearTimeout(timer); // Cleanup on unmount
  // }, []);

  useEffect(
    () => {
      if (frameworkRightType) {
        switch (frameworkRightType) {
          case frameworkRightTypes.pageMedia:
            const { levelMatches } = matches_info ?? {}
            const _matches = {
              ...levelMatches
            }
            framework_handlers.handleShow_frameworkRightData(_matches, view, viewItem.key, viewItem)
            break;
          default:
          // nothing
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps  
    }, [frameworkRightType, matches_info]
  )

  useEffect(() => {
    parent_fns.addToContexts({ sportsSeason_handlers: { ...sportsSeason_handlers } })
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [])

  useEffect(() => {
    if (matches_info && pathViews.sports) {
      sportsSeason_handlers.handleAmmend_seasonMatchesFromDbResults(realtimeDocumentTypes.matchDay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [matches_info, results_matchDay])

  useEffect(() => {
    if (matches_info && pathViews.sports) {
      sportsSeason_handlers.handleAmmend_seasonMatchesFromDbResults(realtimeDocumentTypes.realtime)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [matches_info, results_realtime])

  useEffect(() => {
    if (!_.isEmpty(districts_org)) {
      sportsSeason_handlers.handleSet_sportsSeasonState(districts_org, 'organization', 'districts_info')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [districts_org])

  useEffect(() => {
    if (!_.isEmpty(schools_district)) {
      sportsSeason_handlers.handleSet_sportsSeasonState(schools_district, 'schools', 'schools_info')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [schools_district])

  useEffect(() => {
    if (!_.isEmpty(sportTeams)) {
      sportsSeason_handlers.handleSet_sportsSeasonState(sportTeams, 'sportTeams', 'teams_info')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [sportTeams])

  useEffect(() => {
    if (latestSeason && header_handlers) {
      const _latest = `${latestSeason - 1}/${latestSeason}`;
      if (sportsMode.sportLanding) {
        header_handlers.handleSportsCaption(_latest)
      } else {
        header_handlers.handleSportsCaption(_.startCase(_sportsModeKey) + ' ' + _latest)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [latestSeason, pathViews])

  useEffect(() => {
    const { name: accessEmail } = accessClone ?? {}
    const _email = accessEmail ? accessEmail : email_appUser
    let teamsAccess;
    if (sportsAccess_teams) {
      teamsAccess = findItemsByName(sportsAccess_teams, _email)
    }
    if ((access_sports && sportsKey && access_sports[sportsKey]) || sportsAccess_sports || sportAccess || teamsAccess) {
      sportsSeason_handlers.handleSet_sportsSeasonState(access_sports && access_sports[sportsKey], 'access_sports')
      const sportsAccess = findItemsByName(access_sports, _email)
      const schoolsAccess = findItemsByName(access_schools, _email)
      const _sportAccess = findItemsByName(sportAccess, _email)
      const appUserAccess = {
        teamsAccess: _.keys(teamsAccess),
        sportsAccess: _.keys(sportsAccess),
        schoolsAccess: _.keys(schoolsAccess),
        sportAccess: _.keys(_sportAccess),
      }
      appUser_handlers.handleUpdateAccess(appUserAccess)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [sportsAccess_teams, access_sports, access_schools, sportsAccess_sports, sportsKey, accessClone])

  useEffect(() => {
    if (gls_scores && sportsKey && gls_scores[sportsKey]) {
      sportsSeason_handlers.handleSet_sportsSeasonState(gls_scores[sportsKey], 'links_score')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [gls_scores, sportsKey])

  useEffect(() => {
    if (sportsAccess_sports_d?.[sportsKey]) {
      const xxx = {
        [sportsKey]: sportsAccess_sports_d?.[sportsKey]
      }
      sportsSeason_handlers.handleSet_sportsSeasonState(xxx, 'sportAccess')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [sportsAccess_sports_d, sportsKey])


  return sportsSeason_state ?
    <SportsSeasonContext.Provider value={{ sportsSeason_state, sportsSeason_handlers, appComponent: 'sportsSeason' }}>
      {props.children}
    </SportsSeasonContext.Provider>
    :
    <div></div>
}
